<template>
  <div class="page-container">
    <div class="content-wrapper">
      <AppNavbar>
        <button class="sign-out-btn" @click="handleSignOutClick">Sign Out</button>
      </AppNavbar>
      <div class="spacer"></div>

      <div class="account-page">
        <div class="content-card">
          <div class="welcome-container">
            <p class="welcome-text">
              Welcome {{ fullName }}
            </p>
          </div>

          <table class="status-table">
            <thead>
              <tr>
                <th>Address</th>
                <th style="text-align: left;">
                  <div class="status-header">
                    <span>Status</span>
                  </div>
                </th> 
              </tr>
            </thead>
            <tbody>
              <tr v-for="account in emailAccounts" :key="account.address">
                <td>{{ account.address }}</td>
                <td style="text-align: left;">
                                    <div class="sync-status-container">
                    <span 
                      class="sync-status" 
                      :class="[getSyncStatusClass(account.sync_status), { 'tooltip-container': account.sync_status === 'error' }]"
                    >
                      {{ getSyncStatusText(account.sync_status) }}
                      <span v-if="account.sync_status === 'error'" class="tooltip-content">
                        Please contact us on info@inboxmaster.ai or sync again.
                      </span>
                    </span>
                    <div class="button-space">
                      <span 
                        v-if="account.sync_status === 'not_synced' || account.sync_status === 'error'"
                        class="sync-text" 
                        @click="handleSyncClick(account.address)"
                      >
                        Sync
                      </span>
                      <div 
                        v-if="account.sync_status === 'in_progress'"
                        class="sync-progress-info"
                      >
                        <div class="sync-percentage">{{ account.sync_percentage }}%</div>
                        <div class="estimated-time">{{ formatEstimatedTime(account.estimated_time) }}</div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
              <tr v-if="emailAccounts.length === 0">
                <td colspan="2" class="no-accounts-message">
                  No email accounts found. Add an account below.
                </td>
              </tr>
            </tbody>
          </table>

          <GoogleAddEmailAccount @accountAdded="handleAccountAdded" />
        </div>

        <div class="content-card subscription-card">
          <div class="welcome-container">
            <p class="welcome-text">
              Subscription Details
            </p>
          </div>
          <div class="subscription-grid">
            <div class="subscription-item">
              <span class="label">Subscription Type</span>
              <span class="value" :class="{ 'status-active': isPaying }">
                {{ isPaying ? 'Premium' : 'Free' }}
                <button v-if="!isPaying" class="btn upgrade-btn tooltip-container" @click="handleUpgradeClick" aria-label="Upgrade">
                  <span class="upgrade-icon" aria-hidden="true">👑</span>
                  <span class="upgrade-text">Upgrade to Premium</span>
                  <span class="tooltip-content">Click to upgrade from ${{ monthlyPrice }}€/month.</span>
                </button>
              </span>
            </div>
            <div class="subscription-item" v-if="paymentStatus">
                <span class="label">Subscription Status</span>
              <span class="value">
                {{ paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1) }}
                <button v-if="paymentStatus === 'canceled'" class="btn resub-btn tooltip-container" @click="handleResubscribeClick" aria-label="Resubscribe">
                  <span class="resub-text">👑 Resubscribe</span>
                  <span class="tooltip-content">Reactivate your subscription</span>
                </button>
              </span>
            </div>

            <div class="subscription-item" :class="{ 'full-width-item': !nextPaymentAt }">
              <span class="label">Enable "Inbox powered by inboxmaster.ai" <br>in your signature</span>
              <div class="toggle-container">
                <label class="toggle tooltip-container" :class="{ }" @click="!isPaying && handleUpgradeClick()">
                  <input
                    type="checkbox"
                    v-model="wantsSignature"
                    @change="toggleSignature"
                    :disabled="!isPaying"
                  >
                  <span class="slider" :class="{ 'disabled': !isPaying }"></span>
                  <span v-if="!isPaying" class="tooltip-content">This is a premium feature. Click to upgrade.</span>
                </label>
                <span class="toggle-status" :class="{ 'disabled-text': !isPaying }">
                  {{ wantsSignature ? 'Enabled' : 'Disabled' }}
                </span>
              </div>
            </div>
            
            <div class="subscription-item" v-if="nextPaymentAt">
              <span class="label">{{ paymentStatus === 'canceled' ? 'Active until' : 'Next Payment' }}</span>
              <span class="value">{{ formattedNextPayment }}</span>
            </div>
          </div>
        </div>
        <div class="delete-account-container">
          <span
            v-if="isPaying && paymentStatus !== 'canceled'"
            class="cancel-subscription-text"
            @click="handleCancelSubscription"
          >
            Cancel subscription
          </span>
          <span class="delete-account-text" @click="handleDeleteAccount">
            Delete account
          </span>
        </div>
      </div>
    </div>
    <AppFooter />
    <DeleteAccountModal 
      :show="showDeleteModal"
      :email="primaryEmail"
      @close="showDeleteModal = false"
      @confirm="confirmDeleteAccount"
    />
    <CancelSubscriptionModal
      :show="showCancelSubscriptionModal"
      :nextPaymentAt="nextPaymentAt"
      @close="showCancelSubscriptionModal = false"
      @confirm="confirmCancelSubscription"
    />
    <ResubscribeModal 
      :show="showResubscribeModal"
      :nextPaymentDate="calculateNextPaymentDate()"
      @close="showResubscribeModal = false"
      @confirm="confirmResubscribe"
    />
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { authAPI, subscriptionAPI, userAPI } from "@/services/api";
import { openPopup } from "@/utils/googleAuth";
import AppFooter from "@/components/AppFooter.vue";
import AppNavbar from "@/components/AppNavbar.vue";
import DeleteAccountModal from "@/components/DeleteAccountModal.vue";
import CancelSubscriptionModal from "@/components/CancelSubscriptionModal.vue";
import ResubscribeModal from "@/components/ResubscribeModal.vue";
import GoogleAddEmailAccount from "@/components/GoogleAddEmailAccount.vue";
import { Config } from "@/configs/config";
import { identifyUser, resetMixpanel } from "@/analytics/mixpanel";


export default defineComponent({
  name: "AccountPage",
  components: {
    AppFooter,
    AppNavbar,
    DeleteAccountModal,
    CancelSubscriptionModal,
    ResubscribeModal,
    GoogleAddEmailAccount
  },

  data() {
    return {
      emailAccounts: [],
      fullName: "",
      isPaying: false,
      paymentStatus: "",
      nextPaymentAt: "",
      wantsSignature: false,
      userId: null,
      showDeleteModal: false,
      showCancelSubscriptionModal: false,
      showResubscribeModal: false,
      authStatusInterval: null,
      authStatusBackoff: 2000, // 1 second
      lastHiddenAt: null,
      syncingAnimationState: 0, // 0: "Syncing", 1: "Syncing.", 2: "Syncing..", 3: "Syncing..."
      syncingAnimationInterval: null
    };
  },

  mounted() {
    this.checkAuthStatus();
    this.resetBackoff();
    this.scheduleAuthStatusCheck();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },

  beforeUnmount() {
    if (this.authStatusInterval) {
      clearTimeout(this.authStatusInterval);
    }
    this.stopSyncingAnimation();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },

  methods: {
    async logout() {
      try {
        await authAPI.logout();
      } catch (error) {
        console.error("Error during logout:", error);
      } finally {
        resetMixpanel();
        this.$router.push('/');
      }
    },

    handleSignOutClick() {
      this.logout();
    },

    async handleSyncClick(address) {
      const { popup, authCode } = await openPopup(true, address);
      if(authCode){
        await this.syncUser(authCode, popup);
      }
    },

    async syncUser(authCode, popup) {
      try {
        await authAPI.sync(authCode);
        this.isSynced = true;
        popup.close();
        // Refresh auth status to get updated sync status
        await this.checkAuthStatus();
      } catch (error) {
        popup.close();
        console.error("Error syncing user:", error);
      }
    },

    async checkAuthStatus() {
      try {
        const response = await userAPI.getUser();
        identifyUser(response?.data);
        this.emailAccounts = response.data.email_accounts; // Changed from userEmail to emailAccounts
        this.fullName = response.data.full_name || "User";
        this.isPaying = response.data.is_paying;
        this.wantsSignature = response.data.wants_signature;
        this.paymentStatus = response.data.payments[0]?.status;
        if(this.paymentStatus === 'unpaid'){
          this.paymentStatus = 'active';
        }
        this.nextPaymentAt = response.data.payments[0]?.current_period_end;
        const canceledAt = response.data.payments[0]?.canceled_at;
        this.userId = response.data.user_id;

        if(this.paymentStatus === "canceled"){
          this.paymentStatus = undefined;
          this.nextPaymentAt = undefined;
        }

        if(this.paymentStatus === "active" && canceledAt){
          this.paymentStatus = "canceled";
        }

        // Update syncing animation based on account status
        this.updateSyncingAnimation();
      } catch (error) {
        this.$router.push("/");
        console.log("User not authenticated");
      }
    },

    async toggleSignature() {
      try {
        await userAPI.toggleSignature(this.wantsSignature);
        // Optionally: add success feedback to the user
        console.log("Signature preference updated successfully.");
      } catch (error) {
        console.error("Error updating signature preference:", error);
        // Revert the toggle state visually on error
        this.wantsSignature = !this.wantsSignature;
        // Optionally: add error feedback to the user
      }
    },

    async handleUpgradeClick() {
      if (!this.userId) {
        console.error("User ID is not available for upgrade redirect.");
        return;
      }

      this.$router.push({
        path: "/payment",
        query: {
          address: this.primaryEmail,
          userId: this.userId,
        }
      });
    },

    handleDeleteAccount() {
      this.showDeleteModal = true;
    },

    async confirmDeleteAccount() {
      try {
        // TODO: Implement actual account deletion API call
        await userAPI.deleteAccount();
        this.showDeleteModal = false;
        this.$router.push('/');
      } catch (error) {
        console.error("Error deleting account:", error);
        // TODO: Show error message to user
      }
    },

    handleCancelSubscription() {
      this.showCancelSubscriptionModal = true;
    },

    async confirmCancelSubscription() {
      try {
        // TODO: Implement actual subscription cancellation API call
        await subscriptionAPI.cancel();
        this.showCancelSubscriptionModal = false;
        // Optionally refresh user data or show feedback
        this.resetBackoff();
        this.scheduleAuthStatusCheck();
      } catch (error) {
        console.error("Error cancelling subscription:", error);
        // TODO: Show error message to user
      }
    },

    handleResubscribeClick() {
      this.showResubscribeModal = true;
    },

    async confirmResubscribe() {
      try {
        await subscriptionAPI.resume();
        this.showResubscribeModal = false;
        // Optionally refresh user data or show feedback
        this.resetBackoff();
        this.scheduleAuthStatusCheck();
      } catch (error) {
        console.error("Error resubscribing:", error);
        // TODO: Show error message to user
      }
    },

    scheduleAuthStatusCheck() {
      this.authStatusInterval = setTimeout(async () => {
        await this.checkAuthStatus();
        // Exponential backoff after every call, up to 1 minute
        this.authStatusBackoff = Math.min(this.authStatusBackoff * 2,  20 * 1000);
        this.scheduleAuthStatusCheck();
      }, this.authStatusBackoff);
    },

    handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        this.lastHiddenAt = Date.now();
      } else if (document.visibilityState === 'visible') {
        if (this.lastHiddenAt && Date.now() - this.lastHiddenAt >  60 * 1000) { // 1 minute
            this.resetBackoff();
          this.scheduleAuthStatusCheck();
        }
        this.lastHiddenAt = null;
      }
    },

    resetBackoff() {
      this.authStatusBackoff = 1000;
    },

    calculateNextPaymentDate() {
      // Calculate the next payment date (one month from today)
      const today = new Date();
      const nextMonth = new Date(today);
      nextMonth.setMonth(today.getMonth() + 1);
      return nextMonth.toISOString();
    },

    async handleAccountAdded() {
      // Refresh user data after a new account is added
      await this.checkAuthStatus();
    },



    getSyncStatusClass(status) {
      switch (status) {
        case 'synced':
          return 'synced';
        case 'in_progress':
          return 'in-progress';
        case 'not_synced':
          return 'not-synced';
        case 'error':
          return 'error';
        default:
          return '';
      }
    },

    getSyncStatusText(status) {
      switch (status) {
        case 'synced':
          return 'Synced';
        case 'in_progress':
          return this.getAnimatedSyncingText();
        case 'not_synced':
          return 'Not Synced';
        case 'error':
          return 'Error';
        default:
          return 'Unknown';
      }
    },

    getAnimatedSyncingText() {
      const baseText = 'Syncing';
      const dots = '.'.repeat(this.syncingAnimationState);
      return baseText + dots;
    },

    startSyncingAnimation() {
      if (!this.syncingAnimationInterval) {
        this.syncingAnimationState = 0;
        this.syncingAnimationInterval = setInterval(() => {
          this.syncingAnimationState = (this.syncingAnimationState + 1) % 4;
        }, 500); // Change every 500ms for a nice pace
      }
    },

    stopSyncingAnimation() {
      if (this.syncingAnimationInterval) {
        clearInterval(this.syncingAnimationInterval);
        this.syncingAnimationInterval = null;
      }
    },

    updateSyncingAnimation() {
      const hasInProgressAccounts = this.emailAccounts.some(account => account.sync_status === 'in_progress');
      
      if (hasInProgressAccounts) {
        this.startSyncingAnimation();
      } else {
        this.stopSyncingAnimation();
      }
    },

    getSyncButtonText(status) {
      switch (status) {
        case 'synced':
          return 'Resync';
        case 'in_progress':
          return 'Syncing...';
        case 'not_synced':
          return 'Sync';
        case 'error':
          return 'Retry';
        default:
          return 'Sync';
      }
    },

    formatEstimatedTime(milliseconds) {
      if (!milliseconds || milliseconds <= 0) {
        return 'Calculating...';
      }
      
      const totalSeconds = Math.ceil(milliseconds / 1000);
      const totalMinutes = Math.floor(totalSeconds / 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      
      if (hours > 0) {
        const hourText = hours === 1 ? 'hour' : 'hours';
        const minuteText = minutes === 1 ? 'minute' : 'minutes';
        return minutes > 0 ? `${hours} ${hourText} ${minutes} ${minuteText}` : `${hours} ${hourText}`;
      } else if (minutes > 0) {
        const minuteText = minutes === 1 ? 'minute' : 'minutes';
        return `${minutes} ${minuteText}`;
      } else {
        return '< 1 minute';
      }
    },
  },

  computed: {
    formattedNextPayment() {
      if (!this.nextPaymentAt) return 'Not available';
      return new Date(this.nextPaymentAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    monthlyPrice() {
      return Config.MONTHLY_PRICE;
    },
    primaryEmail() {
      return this.emailAccounts.length > 0 ? this.emailAccounts[0].address : "N/A";
    }
  }
});
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.content-wrapper {
  flex: 1 0 auto;
}

.spacer {
  height: 80px;
}

.account-page {
  padding: 2rem;
  min-height: auto;
  background-color: #f7f7f7;
}

.content-card {
  max-width: 800px;
  width: 90%;
  margin: 0 auto;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.welcome-container {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.welcome-logo {
  height: 50px;
  margin-right: 1rem;
}

.welcome-text {
  font-size: 1.5rem;
  font-weight: 500;
  color: #333;
}

.status-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.status-table thead {
  background-color: #f8f9fa;
}

.status-table th,
.status-table td {
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.status-table th {
  font-weight: 100;
  color: #444;
}

.status-table tr:last-child td {
  border-bottom: none;
}

.status-header {
  display: inline-flex;
  align-items: center;
  width: 188px;
  margin: 0;
  padding: 0;
}

.status-header span {
  position: static;
  margin-left: 0;
  transform: none;
}

.header-button-space {
  width: 60px;
}

.sync-status-container {
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  width: 188px;
  margin: 0;
  padding: 0;
}

.sync-status {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9em;
  background-color: #f0f0f0;
  color: #666;
  width: 100px;
  text-align: center;
  box-sizing: border-box;
  white-space: nowrap;
}

.button-space {
  width: 80px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-left: 8px;
}

.sync-text {
  font-size: 0.8em;
  font-weight: 300;
  color: #ccc;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s;
}

.sync-text:hover {
  color: var(--primary-color);
}

.sync-progress-info {
  font-size: 0.8em;
  font-weight: 300;
  color: #666;
}

.sync-percentage {
  font-weight: 400;
  color: #333;
  margin-bottom: 2px;
}

.estimated-time {
  color: #999;
  font-size: 0.9em;
}

.sync-status.synced {
  background-color: #e3f2e6;
  color: #2e7d32;
}

.sync-status.in-progress {
  background-color: #f0f0f0;
  color: #666;
}

.sync-status.not-synced {
  background-color: #f0f0f0;
  color: #666;
}

.sync-status.error {
  background-color: #ffebee;
  color: #d32f2f;
}

.btn {
  display: inline-block;
  padding: 0.75em 1.5em;
  border: 2px solid var(--primary-color);
  background-color:  var(--primary-color);
  color: white;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
  border-radius: 4px;
  text-decoration: none;
  transition: background-color 0.25s ease, color 0.25s ease;
}

.btn:hover {
  color: white;
  transform: translateY(-3px);
}

.btn:active {
  transform: scale(0.98);
}

.btn:disabled {
  background-color: #e0e0e0;
  border-color: #e0e0e0;
  color: #9e9e9e;
  cursor: not-allowed;
  transform: none;
}

.btn:disabled:hover {
  background-color: #e0e0e0;
  border-color: #e0e0e0;
  color: #9e9e9e;
  transform: none;
}

.sign-out-btn {
  padding: 8px 16px;
  background-color: transparent;
  color: #333;
  border: none;
  cursor: pointer;
  font-size: 14px;
  transition: color 0.2s;
}

.sign-out-btn:hover {
  color: var(--primary-color);
}

.subscription-card {
  margin-top: 2rem;
}

.subscription-title {
  font-size: 1.5rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 1.5rem;
}

.subscription-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.subscription-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.full-width-item {
  grid-column: 1 / -1; /* Span all columns */
}

.subscription-item .label {
  font-size: 0.9rem;
  color: #666;
}

.subscription-item .value {
  font-size: 1.1rem;
  color: #333;
  font-weight: 500;
}

.status-active {
  color: #2e7d32;
}

.toggle-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.toggle {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: var(--primary-color);
}

input:checked + .slider:before {
  transform: translateX(24px);
}

.toggle-status {
  font-size: 0.9rem;
  color: #666;
}

.slider.disabled {
  background-color: #e0e0e0;
  cursor: not-allowed;
}

.slider.disabled:before {
  background-color: #bdbdbd;
}

.disabled-text {
  color: #999;
}

.tooltip-container {
  position: relative;
}

.tooltip-content {
  visibility: hidden;
  opacity: 0;
  width: max-content;
  background-color: #333;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 6px 10px;
  position: absolute;
  z-index: 2;
  bottom: 130%;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: 0.85em !important;
  font-weight: 100 !important;
  line-height: 1.4 !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  transition: opacity 0.2s;
  pointer-events: none;
}

.tooltip-container:hover .tooltip-content,
.tooltip-container:focus-within .tooltip-content {
  visibility: visible;
  opacity: 1;
  pointer-events: auto;
}

.upgrade-btn {
  margin-left: 0.5em;
  padding: 0.25em 0.75em;
  min-width: 0;
  background: var(--primary-color, #BA7BA1);
  color: #fff;
  border: none;
  border-radius: 20px;
  font-size: 0.9em;
  font-weight: 500;
  cursor: pointer;
  transition: background 150ms, color 150ms, box-shadow 150ms, transform 150ms;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(186, 123, 161, 0.10);
  outline: none;
  vertical-align: middle;
}

.upgrade-btn:hover {
  background: #c88ab2;
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 3px 6px rgba(186, 123, 161, 0.15);
}

.upgrade-btn:active {
  background: #a86a91;
  color: #fff;
  transform: scale(0.98);
}

.upgrade-btn:focus {
  outline: none;
  box-shadow: 0 2px 4px rgba(186, 123, 161, 0.10);
}

.upgrade-icon {
  font-size: 1.1em;
  margin-right: 0.25em;
  display: inline-block;
  vertical-align: middle;
}

.upgrade-text {
  display: inline-block;
  vertical-align: middle;
}

.resub-btn {
  margin-left: 0.5em;
  padding: 0.25em 0.75em;
  min-width: 0;
  background: var(--primary-color, #BA7BA1);
  color: #fff;
  border: none;
  border-radius: 20px;
  font-size: 0.9em;
  font-weight: 500;
  cursor: pointer;
  transition: background 150ms, color 150ms, box-shadow 150ms, transform 150ms;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(186, 123, 161, 0.10);
  outline: none;
  vertical-align: middle;
}

.resub-btn:hover {
  background: #c88ab2;
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 3px 6px rgba(186, 123, 161, 0.15);
}

.resub-btn:active {
  background: #a86a91;
  color: #fff;
  transform: scale(0.98);
}

.resub-text {
  display: inline-block;
  vertical-align: middle;
}

@media (max-width: 768px) {
  .spacer {
    height: 0;
  }
  
  .account-page {
    padding: 1rem;
  }
  
  .content-card {
    padding: 1rem;
    width: 100%;
    margin: 0 auto 1rem auto;
  }
  
  .welcome-container {
    flex-direction: column;
    text-align: center;
    margin-bottom: 1rem;
  }
  
  .welcome-logo {
    margin-bottom: 1rem;
    margin-right: 0;
  }
  
  .welcome-text {
    font-size: 1.25rem;
  }
  
  .status-table {
    font-size: 14px;
  }
  
  .status-table th,
  .status-table td {
    padding: 12px 8px;
  }
  
  .status-header {
    width: auto;
  }
  
  .sync-status-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    width: auto;
    min-height: auto;
  }
  
  .sync-status {
    width: auto;
    min-width: 80px;
    font-size: 0.8rem;
    padding: 4px 8px;
  }
  
  .button-space {
    width: auto;
    margin-left: 0;
  }
  
  .subscription-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .subscription-item {
    gap: 0.25rem;
  }
  
  .subscription-item .label {
    font-size: 0.85rem;
  }
  
  .subscription-item .value {
    font-size: 1rem;
  }
  
  .toggle-container {
    gap: 0.5rem;
  }
  
  .upgrade-btn,
  .resub-btn {
    font-size: 0.8rem;
    padding: 0.2em 0.5em;
    margin-left: 0.25em;
  }
  
  .upgrade-icon {
    font-size: 1em;
    margin-right: 0.2em;
  }
  
  .delete-account-container {
    width: 100%;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: 1rem;
    margin-top: 1rem;
  }
  
  .cancel-subscription-text {
    margin-right: 0;
    font-size: 0.85rem;
  }
  
  .delete-account-text {
    font-size: 0.85rem;
  }
}

@media (max-width: 480px) {
  .account-page {
    padding: 0.5rem;
  }
  
  .content-card {
    padding: 0.75rem;
  }
  
  .welcome-text {
    font-size: 1.1rem;
  }
  
  .status-table {
    font-size: 13px;
  }
  
  .status-table th,
  .status-table td {
    padding: 10px 6px;
  }
  
  .sync-status {
    font-size: 0.75rem;
    padding: 3px 6px;
  }
  
  .subscription-item .label {
    font-size: 0.8rem;
  }
  
  .subscription-item .value {
    font-size: 0.95rem;
  }
  
  .upgrade-btn,
  .resub-btn {
    font-size: 0.75rem;
    padding: 0.15em 0.4em;
  }
  
  .tooltip-content {
    font-size: 0.8em !important;
    padding: 4px 8px;
  }
  
  .cancel-subscription-text {
    font-size: 0.8rem;
  }
  
  .delete-account-text {
    font-size: 0.8rem;
  }
}

.delete-account-container {
  max-width: 800px;      /* Match .content-card max-width */
  width: 90%;            /* Match .content-card width */
  margin: 0 auto;        /* Center horizontally like the card */
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
}

.delete-account-text {
  font-size: 0.95rem;
  color: rgba(128, 128, 128, 0.5);
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.2s;
}

.delete-account-text:hover {
  color: #d32f2f;
}

.cancel-subscription-text {
  font-size: 0.95rem;
  color: rgba(128, 128, 128, 0.5);
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.2s;
  margin-right: 1.5rem;
}

.cancel-subscription-text:hover {
  color: #d32f2f;
}

.no-accounts-message {
  text-align: center;
  color: #666;
  padding: 20px;
  font-style: italic;
}

</style> 
